/**
 * CreateCommunityModal — the Memberships "Create a community" chooser
 * (Track 4 Slice E′; POLICY_NAVIGATION_RAILS §7.5(a) resolution 2026-07-09).
 *
 * Renders ONLY for holders of an org Administrator grant (or sysadmins).
 * Child orgs are created under an org the user administers, via that
 * community's own canonical creation surface (Community Settings → Entities
 * — org_level_taxonomy, CHECK constraints, relationships, and the Tier-2 2FA
 * server gate all live there); this chooser only routes. Creator auto-admin
 * per POLICY_COMMUNITY_AUTHORITY §5.1 (server-side).
 *
 * Root organizations stay sysadmin-gated (DEV-977) — that entry point
 * RELOCATED here from the retired /my-communities cards page. Geo
 * communities are never user-created (Manage Geographies only, DEV-1388).
 */

import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import CreateOrganizationModal from "../CreateOrganizationModal/CreateOrganizationModal";
import styles from "./CreateCommunityModal.module.css";

export interface CreateCommunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Orgs the user holds an Administrator grant on (V2 set ∩ org types). */
  administeredOrgs: { guid: string; name: string }[];
  /** Sysadmins additionally get the root-org entry point (DEV-977). */
  isSysAdmin: boolean;
  /** Route to the chosen org's canonical creation surface. */
  onPickOrg: (guid: string) => void;
}

const CreateCommunityModal: React.FC<CreateCommunityModalProps> = ({
  isOpen,
  onClose,
  administeredOrgs,
  isSysAdmin,
  onPickOrg,
}) => {
  const { t } = useAppTranslation("community");
  const [rootOpen, setRootOpen] = useState(false);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t("createCommunity.title", "Create a community")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.title}>{t("createCommunity.title", "Create a community")}</div>
        {administeredOrgs.length > 0 && (
          <>
            <div className={styles.hint}>
              {t(
                "createCommunity.underHint",
                "New communities are created inside an organization you administer:",
              )}
            </div>
            <div className={styles.orgList}>
              {administeredOrgs.map((o) => (
                <button
                  key={o.guid}
                  type="button"
                  className={styles.orgBtn}
                  onClick={() => onPickOrg(o.guid)}
                >
                  {o.name}
                </button>
              ))}
            </div>
          </>
        )}
        {isSysAdmin && (
          <div className={styles.rootBlock}>
            <div className={styles.hint}>
              {t("createCommunity.rootHint", "Platform administration:")}
            </div>
            <button
              type="button"
              className={styles.orgBtn}
              onClick={() => setRootOpen(true)}
            >
              {t("createCommunity.newRootOrg", "New root organization")}
            </button>
          </div>
        )}
        <div className={styles.footer}>
          <button type="button" className={styles.cancel} onClick={onClose}>
            {t("createCommunity.cancel", "Cancel")}
          </button>
        </div>
        <CreateOrganizationModal
          isOpen={rootOpen}
          onClose={() => {
            setRootOpen(false);
            onClose();
          }}
        />
      </div>
    </div>,
    document.body,
  );
};

export default CreateCommunityModal;
